import React, { useState } from 'react';
import styled from 'styled-components';
import apiClient from '../services/api';
import { toast } from 'react-toastify';
import { FiEye, FiEyeOff, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const PreviewSection = styled.div`
  margin: 30px 0;
`;

const SectionTitle = styled.h3`
  color: #424242;
  margin-bottom: 15px;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilePreviewContainer = styled.div`
  margin-bottom: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 15px;
  background: #fafafa;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f5f5f5;
    border-color: #d0d0d0;
  }
`;

const FileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .file-title {
    color: #424242;
    font-size: 0.95rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .button-group {
    display: flex;
    gap: 8px;
  }
`;

const SmallButton = styled.button`
  background: ${props => props.active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff'};
  color: ${props => props.active ? '#fff' : '#667eea'};
  border: 1px solid ${props => props.active ? '#667eea' : '#d0d0d0'};
  padding: 6px 14px;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: 'Segoe UI', sans-serif;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
    background: ${props => props.active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f3f8ff'};
  }
  
  &:disabled {
    background: #f5f5f5;
    border-color: #e0e0e0;
    color: #bdbdbd;
    cursor: not-allowed;
  }
  
  svg {
    font-size: 0.9rem;
  }
`;

const PreviewContent = styled.div`
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const SheetInfo = styled.div`
  background: #e3f2fd;
  color: #1976d2;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  
  .sheet-name {
    font-weight: 700;
  }
`;

const ColumnList = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
  
  .columns-header {
    color: #424242;
    font-size: 0.85rem;
    font-weight: 600;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .columns-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 8px;
  }
  
  .column-badge {
    background: #f3e5f5;
    color: #7b1fa2;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    text-align: center;
  }
`;

const PreviewTable = styled.div`
  overflow-x: auto;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  max-height: 400px;
  overflow-y: auto;
  
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
    
    th {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;
      padding: 10px;
      text-align: left;
      font-weight: 600;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    
    td {
      padding: 8px 10px;
      border-bottom: 1px solid #f0f0f0;
      color: #616161;
    }
    
    tr:hover {
      background: #f9f9f9;
    }
    
    .row-number {
      background: #f5f5f5;
      font-weight: 600;
      color: #9e9e9e;
      text-align: center;
    }
  }
`;

const DataPreview = ({ jobId, files }) => {
  const [previews, setPreviews] = useState({});
  const [loading, setLoading] = useState({});
  const [showPreview, setShowPreview] = useState({});
  const [showColumns, setShowColumns] = useState({});

  const handlePreview = async (fileIndex) => {
    if (showPreview[fileIndex]) {
      setShowPreview(prev => ({ ...prev, [fileIndex]: false }));
      setShowColumns(prev => ({ ...prev, [fileIndex]: false }));
      return;
    }

    if (!previews[fileIndex]) {
      setLoading(prev => ({ ...prev, [fileIndex]: true }));
      
      try {
        const response = await apiClient.get(`/api/v1/preview/${jobId}?file_index=${fileIndex}`);
        setPreviews(prev => ({ ...prev, [fileIndex]: response.data.preview }));
        setShowPreview(prev => ({ ...prev, [fileIndex]: true }));
        toast.success('Preview loaded!');
      } catch (error) {
        console.error('Preview error:', error);
        toast.error('Preview failed: ' + (error.response?.data?.detail || error.message));
      } finally {
        setLoading(prev => ({ ...prev, [fileIndex]: false }));
      }
    } else {
      setShowPreview(prev => ({ ...prev, [fileIndex]: true }));
    }
  };

  const toggleColumns = (fileIndex) => {
    setShowColumns(prev => ({ ...prev, [fileIndex]: !prev[fileIndex] }));
  };

  return (
    <PreviewSection>
      <SectionTitle>📊 Data Preview</SectionTitle>
      
      {files.map((file, index) => (
        <FilePreviewContainer key={index}>
          <FileHeader>
            <span className="file-title">📄 {file.filename}</span>
            <div className="button-group">
              <SmallButton 
                onClick={() => handlePreview(index)}
                disabled={loading[index]}
                active={showPreview[index]}
              >
                {loading[index] ? (
                  <>⏳ Loading...</>
                ) : showPreview[index] ? (
                  <><FiEyeOff /> Hide</>
                ) : (
                  <><FiEye /> Preview</>
                )}
              </SmallButton>
            </div>
          </FileHeader>
          
          {showPreview[index] && previews[index] && (
            <PreviewContent>
              {Object.entries(previews[index]).map(([sheetName, sheetData]) => (
                <div key={sheetName}>
                  <SheetInfo>
                    <span className="sheet-name">📋 {sheetName}</span>
                    <span>📊 {sheetData.total_rows} rows × {sheetData.columns.length} columns</span>
                  </SheetInfo>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <SmallButton onClick={() => toggleColumns(`${index}-${sheetName}`)}>
                      {showColumns[`${index}-${sheetName}`] ? <FiChevronUp /> : <FiChevronDown />}
                      {showColumns[`${index}-${sheetName}`] ? 'Hide' : 'Show'} Columns ({sheetData.columns.length})
                    </SmallButton>
                  </div>
                  
                  {showColumns[`${index}-${sheetName}`] && (
                    <ColumnList>
                      <div className="columns-header">
                        🗂️ Column Names
                      </div>
                      <div className="columns-grid">
                        {sheetData.columns.map((col, idx) => (
                          <div key={idx} className="column-badge">{col}</div>
                        ))}
                      </div>
                    </ColumnList>
                  )}
                  
                  <PreviewTable>
                    <table>
                      <thead>
                        <tr>
                          <th className="row-number">#</th>
                          {sheetData.columns.map((col, idx) => (
                            <th key={idx}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sheetData.rows.map((row, rowIdx) => (
                          <tr key={rowIdx}>
                            <td className="row-number">{rowIdx + 1}</td>
                            {sheetData.columns.map((col, colIdx) => (
                              <td key={colIdx}>{row[col]?.toString() || '-'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PreviewTable>
                </div>
              ))}
            </PreviewContent>
          )}
        </FilePreviewContainer>
      ))}
    </PreviewSection>
  );
};

export default DataPreview;